import type { ClinicRepository } from '@/lib/clinic/repository'

import type {
    AuditEntry,
    ClinicData,
    ClinicSettings,
    Consultation,
    ConsultationInput,
    FollowUp,
    FollowUpInput,
    Medicine,
    Patient,
    PatientInput,
    Prescription,
    PrescriptionInput,
    ReminderActivity,
    ReminderChannel
} from '@/lib/clinic/types'

import { createId, formatPatientCode, formatPrescriptionNumber } from '@/lib/clinic/ids'
import { createEmptyData, createSeedData } from '@/lib/clinic/seed'
import { formatDate, formatDateTime, nowIso } from '@/lib/clinic/datetime'
import { isStorageFullError } from '@/lib/clinic/repository'

import {
    appendHistory,
    changedLabels,
    createAudit,
    summarizeChanges,
    withReason
} from '@/lib/clinic/audit'

interface ActionDependencies {
    getData: () => ClinicData
    commit: (next: ClinicData) => void
    repository: ClinicRepository
    onError: (message: string) => void
}

interface CreateOptions {
    manualDate?: boolean
}

interface ReminderInput {
    patientId: string
    followUpId: string | null
    channel: ReminderChannel
    message: string
}

const PATIENT_LABELS: Partial<Record<keyof Patient, string>> = {
    name: 'name',
    phone: 'phone number',
    age: 'age',
    dateOfBirth: 'date of birth',
    gender: 'gender',
    place: 'place',
    address: 'address',
    medicalHistory: 'medical history',
    allergies: 'allergies',
    notes: 'notes'
}

const CONSULTATION_LABELS: Partial<Record<keyof Consultation, string>> = {
    complaints: 'chief complaint',
    complaintNote: 'complaint details',
    findings: 'clinical findings',
    diagnosis: 'diagnosis',
    treatments: 'treatment',
    treatmentNote: 'treatment details',
    notes: "doctor's notes"
}

const PRESCRIPTION_LABELS: Partial<Record<keyof Prescription, string>> = {
    consultationId: 'linked consultation',
    complaint: 'chief complaint',
    diagnosis: 'diagnosis',
    medicines: 'medicines',
    advice: 'advice',
    notes: "doctor's notes"
}

const FOLLOW_UP_LABELS: Partial<Record<keyof FollowUp, string>> = {
    consultationId: 'related consultation',
    treatment: 'previous treatment',
    notes: 'follow-up notes'
}

const cleanList = (values: string[]): string[] => {
    return Array.from(new Set(values.map(value => value.trim()).filter(Boolean)))
}

const sameMinute = (a: string, b: string): boolean => {
    return Math.floor(new Date(a).getTime() / 60_000) === Math.floor(new Date(b).getTime() / 60_000)
}

const normalizePatient = (input: PatientInput): PatientInput => ({
    name: input.name.trim().replace(/\s+/g, ' '),
    phone: input.phone.trim(),
    age: input.dateOfBirth ? null : input.age,
    dateOfBirth: input.dateOfBirth || null,
    gender: input.gender,
    place: input.place.trim(),
    address: input.address.trim(),
    medicalHistory: input.medicalHistory.trim(),
    allergies: input.allergies.trim(),
    notes: input.notes.trim()
})

const normalizeConsultation = (input: ConsultationInput): ConsultationInput => ({
    consultedAt: input.consultedAt,
    complaints: cleanList(input.complaints),
    complaintNote: input.complaintNote.trim(),
    findings: input.findings.trim(),
    diagnosis: input.diagnosis.trim(),
    treatments: cleanList(input.treatments),
    treatmentNote: input.treatmentNote.trim(),
    notes: input.notes.trim()
})

const normalizeMedicines = (medicines: Medicine[]): Medicine[] => {
    return medicines
        .map(medicine => ({
            id: medicine.id || createId(),
            name: medicine.name.trim(),
            dosage: medicine.dosage.trim(),
            frequency: medicine.frequency.trim(),
            duration: medicine.duration.trim(),
            instructions: medicine.instructions.trim()
        }))
        .filter(medicine => medicine.name)
}

const normalizePrescription = (input: PrescriptionInput): PrescriptionInput => ({
    consultationId: input.consultationId || null,
    prescribedAt: input.prescribedAt,
    complaint: input.complaint.trim(),
    diagnosis: input.diagnosis.trim(),
    medicines: normalizeMedicines(input.medicines),
    advice: input.advice.trim(),
    notes: input.notes.trim()
})

const normalizeFollowUp = (input: FollowUpInput): FollowUpInput => ({
    consultationId: input.consultationId || null,
    treatment: input.treatment.trim(),
    dueDate: input.dueDate,
    notes: input.notes.trim()
})

export const createClinicActions = ({ getData, commit, repository, onError }: ActionDependencies) => {
    const persist = (...tasks: Array<Promise<unknown>>): void => {
        Promise.all(tasks).catch((error: unknown) => {
            console.error(error)
            onError(isStorageFullError(error)
                ? 'Storage on this device is full. Remove the clinic logo or older records, then try again.'
                : 'The last change could not be saved on this device. Please try again.')
        })
    }

    const registerPatient = (input: PatientInput): Patient => {
        const data = getData()
        const now = nowIso()
        const counters = { ...data.counters, patient: data.counters.patient + 1 }

        const patient: Patient = {
            id: createId(),
            code: formatPatientCode(counters.patient),
            ...normalizePatient(input),
            createdAt: now,
            updatedAt: now,
            history: [createAudit('created', 'Patient registered', now)]
        }

        commit({ ...data, patients: [patient, ...data.patients], counters })
        persist(repository.saveRecord('patients', patient), repository.saveCounters(counters))

        return patient
    }

    const updatePatient = (id: string, input: PatientInput): Patient | null => {
        const data = getData()
        const existing = data.patients.find(patient => patient.id === id)
        if (!existing) return null

        const merged: Patient = { ...existing, ...normalizePatient(input) }
        const labels = changedLabels(existing, merged, PATIENT_LABELS)
        if (labels.length === 0) return existing

        const now = nowIso()
        const patient: Patient = {
            ...merged,
            updatedAt: now,
            history: appendHistory(existing.history, [createAudit('updated', summarizeChanges(labels), now)])
        }

        commit({ ...data, patients: data.patients.map(item => (item.id === id ? patient : item)) })
        persist(repository.saveRecord('patients', patient))

        return patient
    }

    const deletePatient = (id: string): void => {
        const data = getData()
        const consultationIds = data.consultations.filter(item => item.patientId === id).map(item => item.id)
        const prescriptionIds = data.prescriptions.filter(item => item.patientId === id).map(item => item.id)
        const followUpIds = data.followUps.filter(item => item.patientId === id).map(item => item.id)
        const reminderIds = data.reminders.filter(item => item.patientId === id).map(item => item.id)

        commit({
            ...data,
            patients: data.patients.filter(item => item.id !== id),
            consultations: data.consultations.filter(item => item.patientId !== id),
            prescriptions: data.prescriptions.filter(item => item.patientId !== id),
            followUps: data.followUps.filter(item => item.patientId !== id),
            reminders: data.reminders.filter(item => item.patientId !== id)
        })

        persist(
            repository.deleteRecords('patients', [id]),
            repository.deleteRecords('consultations', consultationIds),
            repository.deleteRecords('prescriptions', prescriptionIds),
            repository.deleteRecords('followUps', followUpIds),
            repository.deleteRecords('reminders', reminderIds)
        )
    }

    const createConsultation = (patientId: string, input: ConsultationInput, options: CreateOptions = {}): Consultation => {
        const data = getData()
        const now = nowIso()
        const values = normalizeConsultation(input)

        const consultation: Consultation = {
            id: createId(),
            patientId,
            ...values,
            createdAt: now,
            updatedAt: now,
            history: [
                createAudit(
                    'created',
                    options.manualDate ? `Consultation recorded with the date set to ${formatDateTime(values.consultedAt)}` : 'Consultation recorded',
                    now
                )
            ]
        }

        commit({ ...data, consultations: [consultation, ...data.consultations] })
        persist(repository.saveRecord('consultations', consultation))

        return consultation
    }

    const updateConsultation = (id: string, input: ConsultationInput, reason?: string): Consultation | null => {
        const data = getData()
        const existing = data.consultations.find(item => item.id === id)
        if (!existing) return null

        const values = normalizeConsultation(input)
        const now = nowIso()
        const entries: AuditEntry[] = []

        if (sameMinute(values.consultedAt, existing.consultedAt)) {
            values.consultedAt = existing.consultedAt
        }
        else {
            entries.push(createAudit(
                'date-corrected',
                withReason(`Consultation date corrected from ${formatDateTime(existing.consultedAt)} to ${formatDateTime(values.consultedAt)}`, reason),
                now
            ))
        }

        const merged: Consultation = { ...existing, ...values }
        const labels = changedLabels(existing, merged, CONSULTATION_LABELS)

        if (labels.length > 0) entries.push(createAudit('updated', summarizeChanges(labels), now))
        if (entries.length === 0) return existing

        const consultation: Consultation = { ...merged, updatedAt: now, history: appendHistory(existing.history, entries) }

        commit({ ...data, consultations: data.consultations.map(item => (item.id === id ? consultation : item)) })
        persist(repository.saveRecord('consultations', consultation))

        return consultation
    }

    const deleteConsultation = (id: string): void => {
        const data = getData()
        const now = nowIso()

        const unlinkedPrescriptions = data.prescriptions
            .filter(item => item.consultationId === id)
            .map(item => ({
                ...item,
                consultationId: null,
                updatedAt: now,
                history: appendHistory(item.history, [createAudit('updated', 'Linked consultation was deleted', now)])
            }))

        const unlinkedFollowUps = data.followUps
            .filter(item => item.consultationId === id)
            .map(item => ({
                ...item,
                consultationId: null,
                updatedAt: now,
                history: appendHistory(item.history, [createAudit('updated', 'Related consultation was deleted', now)])
            }))

        const prescriptionMap = new Map(unlinkedPrescriptions.map(item => [item.id, item]))
        const followUpMap = new Map(unlinkedFollowUps.map(item => [item.id, item]))

        commit({
            ...data,
            consultations: data.consultations.filter(item => item.id !== id),
            prescriptions: data.prescriptions.map(item => prescriptionMap.get(item.id) ?? item),
            followUps: data.followUps.map(item => followUpMap.get(item.id) ?? item)
        })

        persist(
            repository.deleteRecords('consultations', [id]),
            ...unlinkedPrescriptions.map(item => repository.saveRecord('prescriptions', item)),
            ...unlinkedFollowUps.map(item => repository.saveRecord('followUps', item))
        )
    }

    const createPrescription = (patientId: string, input: PrescriptionInput, options: CreateOptions = {}): Prescription => {
        const data = getData()
        const now = nowIso()
        const counters = { ...data.counters, prescription: data.counters.prescription + 1 }
        const values = normalizePrescription(input)

        const prescription: Prescription = {
            id: createId(),
            number: formatPrescriptionNumber(counters.prescription),
            patientId,
            ...values,
            createdAt: now,
            updatedAt: now,
            history: [
                createAudit(
                    'created',
                    options.manualDate ? `Prescription created with the date set to ${formatDateTime(values.prescribedAt)}` : 'Prescription created',
                    now
                )
            ]
        }

        commit({ ...data, prescriptions: [prescription, ...data.prescriptions], counters })
        persist(repository.saveRecord('prescriptions', prescription), repository.saveCounters(counters))

        return prescription
    }

    const updatePrescription = (id: string, input: PrescriptionInput, reason?: string): Prescription | null => {
        const data = getData()
        const existing = data.prescriptions.find(item => item.id === id)
        if (!existing) return null

        const values = normalizePrescription(input)
        const now = nowIso()
        const entries: AuditEntry[] = []

        if (sameMinute(values.prescribedAt, existing.prescribedAt)) {
            values.prescribedAt = existing.prescribedAt
        }
        else {
            entries.push(createAudit(
                'date-corrected',
                withReason(`Prescription date corrected from ${formatDateTime(existing.prescribedAt)} to ${formatDateTime(values.prescribedAt)}`, reason),
                now
            ))
        }

        const merged: Prescription = { ...existing, ...values }
        const labels = changedLabels(existing, merged, PRESCRIPTION_LABELS)

        if (labels.length > 0) entries.push(createAudit('updated', summarizeChanges(labels), now))
        if (entries.length === 0) return existing

        const prescription: Prescription = { ...merged, updatedAt: now, history: appendHistory(existing.history, entries) }

        commit({ ...data, prescriptions: data.prescriptions.map(item => (item.id === id ? prescription : item)) })
        persist(repository.saveRecord('prescriptions', prescription))

        return prescription
    }

    const deletePrescription = (id: string): void => {
        const data = getData()

        commit({ ...data, prescriptions: data.prescriptions.filter(item => item.id !== id) })
        persist(repository.deleteRecords('prescriptions', [id]))
    }

    const createFollowUp = (patientId: string, input: FollowUpInput): FollowUp => {
        const data = getData()
        const now = nowIso()
        const values = normalizeFollowUp(input)

        const followUp: FollowUp = {
            id: createId(),
            patientId,
            ...values,
            status: 'pending',
            completedAt: null,
            createdAt: now,
            updatedAt: now,
            history: [createAudit('created', `Follow-up scheduled for ${formatDate(values.dueDate)}`, now)]
        }

        commit({ ...data, followUps: [followUp, ...data.followUps] })
        persist(repository.saveRecord('followUps', followUp))

        return followUp
    }

    const saveFollowUp = (followUp: FollowUp): FollowUp => {
        const data = getData()

        commit({ ...data, followUps: data.followUps.map(item => (item.id === followUp.id ? followUp : item)) })
        persist(repository.saveRecord('followUps', followUp))

        return followUp
    }

    const updateFollowUp = (id: string, input: FollowUpInput, reason?: string): FollowUp | null => {
        const existing = getData().followUps.find(item => item.id === id)
        if (!existing) return null

        const values = normalizeFollowUp(input)
        const now = nowIso()
        const entries: AuditEntry[] = []

        if (values.dueDate !== existing.dueDate) {
            entries.push(createAudit(
                'rescheduled',
                withReason(`Returning date changed from ${formatDate(existing.dueDate)} to ${formatDate(values.dueDate)}`, reason),
                now
            ))
        }

        const merged: FollowUp = { ...existing, ...values }
        const labels = changedLabels(existing, merged, FOLLOW_UP_LABELS)

        if (labels.length > 0) entries.push(createAudit('updated', summarizeChanges(labels), now))
        if (entries.length === 0) return existing

        return saveFollowUp({ ...merged, updatedAt: now, history: appendHistory(existing.history, entries) })
    }

    const rescheduleFollowUp = (id: string, dueDate: string, reason?: string): FollowUp | null => {
        const existing = getData().followUps.find(item => item.id === id)
        if (!existing || existing.dueDate === dueDate) return existing ?? null

        const now = nowIso()

        return saveFollowUp({
            ...existing,
            dueDate,
            updatedAt: now,
            history: appendHistory(existing.history, [
                createAudit('rescheduled', withReason(`Rescheduled from ${formatDate(existing.dueDate)} to ${formatDate(dueDate)}`, reason), now)
            ])
        })
    }

    const completeFollowUp = (id: string): FollowUp | null => {
        const existing = getData().followUps.find(item => item.id === id)
        if (!existing || existing.status === 'completed') return existing ?? null

        const now = nowIso()

        return saveFollowUp({
            ...existing,
            status: 'completed',
            completedAt: now,
            updatedAt: now,
            history: appendHistory(existing.history, [createAudit('completed', 'Marked as completed', now)])
        })
    }

    const reopenFollowUp = (id: string): FollowUp | null => {
        const existing = getData().followUps.find(item => item.id === id)
        if (!existing || existing.status === 'pending') return existing ?? null

        const now = nowIso()

        return saveFollowUp({
            ...existing,
            status: 'pending',
            completedAt: null,
            updatedAt: now,
            history: appendHistory(existing.history, [createAudit('reopened', 'Moved back to pending', now)])
        })
    }

    const deleteFollowUp = (id: string): void => {
        const data = getData()
        const linkedReminders = data.reminders
            .filter(item => item.followUpId === id)
            .map(item => ({ ...item, followUpId: null }))
        const reminderMap = new Map(linkedReminders.map(item => [item.id, item]))

        commit({
            ...data,
            followUps: data.followUps.filter(item => item.id !== id),
            reminders: data.reminders.map(item => reminderMap.get(item.id) ?? item)
        })

        persist(
            repository.deleteRecords('followUps', [id]),
            ...linkedReminders.map(item => repository.saveRecord('reminders', item))
        )
    }

    const logReminder = (input: ReminderInput): ReminderActivity => {
        const data = getData()

        const reminder: ReminderActivity = {
            id: createId(),
            patientId: input.patientId,
            followUpId: input.followUpId,
            channel: input.channel,
            message: input.message.trim(),
            at: nowIso()
        }

        commit({ ...data, reminders: [reminder, ...data.reminders] })
        persist(repository.saveRecord('reminders', reminder))

        return reminder
    }

    const updateSettings = (settings: ClinicSettings): void => {
        const data = getData()

        commit({ ...data, settings })
        persist(repository.saveSettings(settings))
    }

    const resetToSampleData = (): void => {
        const data = getData()
        const next: ClinicData = { ...createSeedData(new Date()), settings: data.settings }

        commit(next)
        persist(repository.replaceAll(next))
    }

    const clearAllRecords = (): void => {
        const data = getData()
        const next: ClinicData = { ...createEmptyData(), settings: data.settings }

        commit(next)
        persist(repository.replaceAll(next))
    }

    return {
        registerPatient,
        updatePatient,
        deletePatient,
        createConsultation,
        updateConsultation,
        deleteConsultation,
        createPrescription,
        updatePrescription,
        deletePrescription,
        createFollowUp,
        updateFollowUp,
        rescheduleFollowUp,
        completeFollowUp,
        reopenFollowUp,
        deleteFollowUp,
        logReminder,
        updateSettings,
        resetToSampleData,
        clearAllRecords
    }
}

export type ClinicActions = ReturnType<typeof createClinicActions>
